import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Ajv from 'ajv';
import {
  DraftReferenceState,
  testDraftConnection,
  TestConnectionResult,
  stableStringify,
} from 'api/referenceOptions';
import { DRAFT_RESET_DEBOUNCE_MS } from './SchemaFormTemplates/ReferenceSelectWidget';
import { isEphemeralExecutionEnabled } from './ephemeralExecution';

// strict:false — integration schemas carry non-standard keywords (gundi:reference, ui:*).
const ajv = new Ajv({ strict: false, allErrors: true });

type Action = {
  id: string;
  type: string;
  value: string;
  name?: string;
  schema?: {
    required?: string[];
    properties?: Record<string, { default?: unknown }>;
    [k: string]: unknown;
  };
};

// `actions` stays permissive: portal callers pass concrete ProviderType /
// DestinationType shapes whose Action fields don't quite match this file's
// Action type (schema is RJSFSchema, not the narrower one below). Widening
// here is the seam that keeps the caller ergonomic.
type ProviderTypeLike = {
  actions?: any[];
  // The integration type's slug, checked against the ephemeral whitelist.
  value?: string;
};

type UseEphemeralTestConnectionArgs = {
  providerType: ProviderTypeLike | null | undefined;
  draftReference: DraftReferenceState | undefined;
  authToken: string | undefined;
};

export type TestConnectionState = {
  isTestable: boolean;
  canTest: boolean;
  disabledReason: string;
  isLoading: boolean;
  result: TestConnectionResult | null;
  runTest: () => Promise<void>;
};

export function selectAuthAction(
  actions: Action[] | undefined,
): Action | undefined {
  return (actions ?? []).find((a) => a.type === 'auth');
}

/** True when the auth action's schema (including if/then/else, oneOf, anyOf) is
 *  satisfied by the current draft. AJV's `required` only checks key presence, so
 *  empty-string values are stripped first to make blank-but-present fields
 *  surface as missing. Only the required verdict gates; type/format errors are
 *  left to the runner. */
export function isAuthComplete(
  actions: Action[] | undefined,
  draft: DraftReferenceState | undefined,
): boolean {
  if (!draft) return false;
  const authAction = selectAuthAction(actions);
  if (!authAction) return true;
  const draftAuth =
    draft.configurations.find((c) => c.actionType === 'auth') ??
    draft.configurations.find((c) => c.actionValue === authAction.value);
  if (!draftAuth) return false;

  const data = draftAuth.data ?? {};
  const schema = authAction.schema;
  if (!schema) return true;

  const nonEmptyData: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (v !== undefined && v !== null && v !== '') nonEmptyData[k] = v;
  }
  try {
    const validate = ajv.compile(schema);
    validate(nonEmptyData);
    return !(validate.errors ?? []).some((e) => e.keyword === 'required');
  } catch {
    // Uncompilable schema: fall through, let the runner be the source of truth.
    return true;
  }
}

export function useEphemeralTestConnection({
  providerType,
  draftReference,
  authToken,
}: UseEphemeralTestConnectionArgs): TestConnectionState {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TestConnectionResult | null>(null);

  // Gating the probe rather than isTestable alone: canTest, the disabled
  // tooltip and runTest's early return all key off it, so a non-whitelisted
  // technology can't reach the endpoint from any of them.
  const probe = useMemo(
    () =>
      isEphemeralExecutionEnabled(providerType?.value)
        ? selectAuthAction(providerType?.actions)
        : undefined,
    [providerType?.value, providerType?.actions],
  );
  const isTestable = !!probe;
  const authComplete = useMemo(
    () => isAuthComplete(providerType?.actions, draftReference),
    [providerType?.actions, draftReference],
  );

  const canTest =
    isTestable && authComplete && !!authToken && !!draftReference && !isLoading;

  const disabledReason = (() => {
    // The button's own label says it's testing, so there's nothing to add.
    if (isLoading) return '';
    if (!isTestable) return t('Test unavailable for this integration');
    if (!draftReference) return t('Select a workspace first');
    if (!authToken) return t('Fill in credentials to test');
    if (!authComplete) return t('Fill in credentials to test');
    return '';
  })();

  // Drop late responses on unmount and on each fresh runTest. Deliberately NOT
  // on every draftReference identity change: the memo reassembles per keystroke,
  // which would cancel a test the user started while still typing.
  const runIdRef = useRef(0);
  useEffect(
    () => () => {
      runIdRef.current++;
    },
    [],
  );

  // Only invalidate a completed result when the auth data itself changes
  // (JSON identity, not object identity, so a memo rebuild doesn't count),
  // and debounce so per-keystroke edits don't fire this per-key. Pull/push
  // section edits leave the last "Valid Credentials" verdict alone. Runs
  // never cancel in-flight — only clears the stale-badge state.
  const authKey = useMemo(() => {
    if (!draftReference) return '';
    const authValue = draftReference.authValue ?? 'auth';
    const auth =
      draftReference.configurations.find((c) => c.actionType === 'auth') ??
      draftReference.configurations.find((c) => c.actionValue === authValue);
    // stableStringify (order-insensitive) so an rjsf re-render that reshuffles
    // property order doesn't fire a spurious reset — that would wipe the
    // "Valid Credentials" badge without any real user edit.
    return auth ? stableStringify(auth.data ?? {}) : '';
  }, [draftReference]);
  const [debouncedAuthKey, setDebouncedAuthKey] = useState(authKey);
  useEffect(() => {
    if (debouncedAuthKey === authKey) return;
    const timeoutId = setTimeout(
      () => setDebouncedAuthKey(authKey),
      DRAFT_RESET_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [authKey, debouncedAuthKey]);
  const prevAuthKeyRef = useRef(debouncedAuthKey);
  useEffect(() => {
    if (prevAuthKeyRef.current === debouncedAuthKey) return;
    prevAuthKeyRef.current = debouncedAuthKey;
    // Prior verdict is stale. Any in-flight test keeps running: its verdict
    // still describes the credentials the user clicked with.
    setResult(null);
  }, [debouncedAuthKey]);

  const runTest = useCallback(async () => {
    if (!probe || !authToken || !draftReference) return;
    const runId = ++runIdRef.current;
    setIsLoading(true);
    setResult(null);
    const r = await testDraftConnection(authToken, probe.value, draftReference);
    if (runIdRef.current !== runId) return;
    setResult(r);
    setIsLoading(false);
    // No toast: DraftTestActionButton renders the verdict next to the button.
  }, [probe, authToken, draftReference]);

  return { isTestable, canTest, disabledReason, isLoading, result, runTest };
}
